import { useContext, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTheme } from "@mui/material/styles";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftAvatar from "components/SoftAvatar";
import SoftButton from "components/SoftButton";
import DefaultUserAvatar from "layouts/pages/account/components/Header/components/DefaultUserAvatar";
import { TenantContext } from "context/AppProvider/TenantContext";
import { useRoles } from "hooks/useRoles";
import Settings from "layouts/pages/account/company/components/Settings";
import { THEME_COLORS } from "constants/theme";

function UserAvatar({ user }) {
  if (user?.image?.trim()) {
    return <SoftAvatar src={user.image} alt={`${user.first_name} ${user.last_name}`} size="sm" />;
  }

  return (
    <SoftAvatar size="sm">
      <DefaultUserAvatar
        firstName={user?.first_name}
        lastName={user?.last_name}
        open={false}
        size="xs"
        border="unset"
      />
    </SoftAvatar>
  );
}

function InfoRow({ label, value, isDarkMode, last = false }) {
  return (
    <SoftBox
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "minmax(140px, 32%) minmax(0, 1fr)" },
        alignItems: "center",
        gap: { xs: 0.75, sm: 2 },
        py: 2,
        borderBottom: last ? "none" : isDarkMode
          ? "1px solid rgba(255,255,255,0.09)"
          : "1px solid rgba(0,0,0,0.09)",
      }}
    >
      <SoftTypography variant="button" color="text" fontWeight="medium">
        {label}
      </SoftTypography>
      <SoftBox sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
        {typeof value === "string" || typeof value === "number" ? (
          <SoftTypography variant="button" className="white-to-dark" fontWeight="regular">
            {value || "—"}
          </SoftTypography>
        ) : value}
      </SoftBox>
    </SoftBox>
  );
}

function CompanyInfo({ isEditing = false, setIsEditing }) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const { pathname } = useLocation();
  const { tenantData } = useContext(TenantContext);
  const { roles } = useRoles(false);
  const { users } = useSelector((store) => store.portal.users);

  const companyUsers = useMemo(() => Array.isArray(users) ? users.slice(0, 5) : [], [users]);
  const ownerRole = useMemo(() => roles?.find((role) => role.name === "Owner"), [roles]);
  const companyOwner = useMemo(
    () => users?.find((user) => user.role_id === ownerRole?.id),
    [users, ownerRole]
  );

  if (pathname !== "/administration/company" || !tenantData) return null;

  const location = [
    tenantData.address_line_1,
    tenantData.address_line_2,
    [tenantData.city, tenantData.state, tenantData.zip].filter(Boolean).join(", "),
    tenantData.country,
  ].filter((part) => part && String(part).toLowerCase() !== "null");

  return (
    <SoftBox
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1.7fr) minmax(320px, 1fr)" },
        alignItems: "start",
        gap: 3,
      }}
    >
      <SoftBox
        component="section"
        aria-label="Company details"
        sx={{
          minWidth: 0,
          border: isDarkMode ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.12)",
          borderRadius: "8px",
          backgroundColor: isDarkMode ? THEME_COLORS.DARK.SECONDARY_BG : THEME_COLORS.LIGHT.SECONDARY_BG,
          p: { xs: 2.5, sm: 3 },
        }}
      >
        <SoftBox display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={1}>
          <SoftTypography variant="h5" className="white-to-dark" fontWeight="bold">
            Company details
          </SoftTypography>
        </SoftBox>

        {isEditing ? (
          <Settings setIsEditing={setIsEditing} companyOwner={companyOwner} />
        ) : (
          <SoftBox>
            <InfoRow label="Company name" value={tenantData.company_name} isDarkMode={isDarkMode} />
            <InfoRow
              label="Company owner"
              isDarkMode={isDarkMode}
              value={companyOwner ? (
                <SoftBox display="flex" alignItems="center" gap={1.5}>
                  <UserAvatar user={companyOwner} />
                  <SoftTypography variant="button" className="white-to-dark">
                    {`${companyOwner.first_name} ${companyOwner.last_name}`}
                  </SoftTypography>
                </SoftBox>
              ) : "—"}
            />
            <InfoRow label="Phone" value={tenantData.business_phone || "—"} isDarkMode={isDarkMode} />
            <InfoRow label="Email" value={tenantData.email_address || "—"} isDarkMode={isDarkMode} />
            <InfoRow
              label="Location"
              isDarkMode={isDarkMode}
              last
              value={location.length ? (
                <SoftBox>
                  {location.map((part, index) => (
                    <SoftTypography key={`${part}-${index}`} variant="button" className="white-to-dark" sx={{ display: "block" }}>
                      {part}
                    </SoftTypography>
                  ))}
                </SoftBox>
              ) : "—"}
            />
          </SoftBox>
        )}
      </SoftBox>

      <SoftBox
        component="section"
        aria-label="Users"
        sx={{
          minWidth: 0,
          border: isDarkMode ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.12)",
          borderRadius: "8px",
          backgroundColor: isDarkMode ? THEME_COLORS.DARK.SECONDARY_BG : THEME_COLORS.LIGHT.SECONDARY_BG,
          p: { xs: 2.5, sm: 3 },
        }}
      >
        <SoftTypography variant="h5" className="white-to-dark" fontWeight="bold" sx={{ mb: 1 }}>
          Users <SoftTypography component="span" variant="h6" color="text">({users?.length || 0})</SoftTypography>
        </SoftTypography>
        {companyUsers.length ? companyUsers.map((user, index) => (
          <SoftBox
            key={user.id || user.auth0_id || index}
            display="flex"
            alignItems="center"
            sx={{
              gap: 1.5,
              py: 1.5,
              minHeight: 58,
              borderTop: isDarkMode ? "1px solid rgba(255,255,255,0.09)" : "1px solid rgba(0,0,0,0.09)",
            }}
          >
            <UserAvatar user={user} />
            <SoftTypography
              component={Link}
              to={`/account/users/profile-overview/${user.auth0_id}`}
              variant="button"
              className="white-to-dark"
              sx={{ textDecoration: "none", flex: 1, minWidth: 0, overflowWrap: "anywhere" }}
            >
              {`${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email_address || "User"}
            </SoftTypography>
            <SoftBox
              component="span"
              sx={{
                flexShrink: 0,
                border: isDarkMode ? "1px solid rgba(255,255,255,0.18)" : "1px solid rgba(0,0,0,0.18)",
                borderRadius: "4px",
                px: 1,
                py: 0.5,
                fontSize: "0.75rem",
                color: isDarkMode ? THEME_COLORS.DARK.PRIMARY_FONT : THEME_COLORS.LIGHT.PRIMARY_FONT,
              }}
            >
              {roles?.find((role) => role.id === user.role_id)?.name || "User"}
            </SoftBox>
          </SoftBox>
        )) : (
          <SoftTypography variant="button" color="text">No users found.</SoftTypography>
        )}
        <SoftButton component={Link} to="/administration/users" variant="text" color="primary" sx={{ mt: 1, px: 0 }}>
          View all users <ArrowForwardIcon sx={{ ml: 1 }} fontSize="small" />
        </SoftButton>
      </SoftBox>
    </SoftBox>
  );
}

export default CompanyInfo;
